import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import { getEntityEndpoint } from '@/shared/api/openmetadata';
import { removeFqnQuotes } from '@/shared/lib/format';
import {
  generateDisplayNameOperations,
  generateStyleOperations,
  generateTagFieldOperations,
} from '@/shared/lib/patch-operations';
import type { JsonPatchOperation } from '@/shared/types/common';

import type { TagItem } from '@/entities/tags';

import { GLOSSARY_CONFIG } from '@/features/asset-detail/lib/constants';

export type GlossaryTerm = {
  id: string;
  name: string;
  fullyQualifiedName: string;
  displayName?: string;
  description?: string;
  glossary?: {
    id: string;
    name: string;
    fullyQualifiedName: string;
  };
  parent?: {
    id: string;
    name: string;
    fullyQualifiedName: string;
  };
};

export type Glossary = {
  id: string;
  name: string;
  fullyQualifiedName: string;
  displayName?: string;
  description?: string;
};

type GlossariesResponse = {
  data: Glossary[];
  paging: {
    total: number;
  };
};

type GlossaryTermsResponse = {
  data: GlossaryTerm[];
  paging: {
    total: number;
  };
};

export type GlossaryTreeNode = {
  value: string;
  title: string;
  key: string;
  children?: GlossaryTreeNode[];
  isLeaf?: boolean;
  disabled?: boolean;
  description?: string;
};

export const fetchGlossaries = async (): Promise<Glossary[]> => {
  try {
    const response = await openMetadataApiClient.get<GlossariesResponse>('/glossaries', {
      params: { limit: GLOSSARY_CONFIG.API.GLOSSARIES_LIMIT },
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch glossaries:', error);
    throw error;
  }
};

export const buildGlossaryTree = (
  glossaries: Glossary[],
  termsMap: Map<string, GlossaryTerm[]>
): GlossaryTreeNode[] => {
  return glossaries.map((glossary) => {
    const terms = termsMap.get(glossary.id) || [];

    const termNodes: GlossaryTreeNode[] = terms
      .filter((term) => !term.parent)
      .map((term) => buildTermNode(term, terms));

    return {
      value: glossary.fullyQualifiedName,
      title: glossary.displayName || glossary.name,
      key: glossary.fullyQualifiedName,
      children: termNodes,
      isLeaf: termNodes.length === 0,
      disabled: true,
    };
  });
};

const buildTermNode = (term: GlossaryTerm, allTerms: GlossaryTerm[]): GlossaryTreeNode => {
  const children = allTerms.filter((t) => t.parent?.id === term.id);

  const childNodes: GlossaryTreeNode[] = children.map((child) => buildTermNode(child, allTerms));

  return {
    value: term.fullyQualifiedName,
    title: term.displayName || term.name,
    key: term.fullyQualifiedName,
    children: childNodes.length > 0 ? childNodes : undefined,
    isLeaf: childNodes.length === 0,
    description: term.description,
  };
};

export const fetchAllGlossaryTerms = async (): Promise<GlossaryTerm[]> => {
  try {
    const response = await openMetadataApiClient.get<GlossaryTermsResponse>('/glossaryTerms', {
      params: {
        limit: GLOSSARY_CONFIG.API.TERMS_LIMIT,
      },
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch all glossary terms:', error);
    throw error;
  }
};

export const fetchGlossaryTree = async (): Promise<GlossaryTreeNode[]> => {
  try {
    const [glossaries, allTerms] = await Promise.all([fetchGlossaries(), fetchAllGlossaryTerms()]);

    const termsMap = new Map<string, GlossaryTerm[]>();
    allTerms.forEach((term) => {
      if (term.glossary?.id) {
        const existing = termsMap.get(term.glossary.id) || [];
        termsMap.set(term.glossary.id, [...existing, term]);
      }
    });

    return buildGlossaryTree(glossaries, termsMap);
  } catch (error) {
    console.error('Failed to fetch glossary tree:', error);
    throw error;
  }
};

export const updateAssetGlossaryTerms = async (
  entityType: string,
  entityId: string,
  patchOperations: JsonPatchOperation[]
): Promise<void> => {
  try {
    const endpoint = getEntityEndpoint(entityType);
    await openMetadataApiClient.patch(`/${endpoint}/${entityId}`, patchOperations, {
      headers: {
        'Content-Type': 'application/json-patch+json',
      },
    });
  } catch (error) {
    console.error('Failed to update asset glossary terms:', error);
    throw error;
  }
};

const createGlossaryTermFromFQN = (
  fqn: string,
  glossaryTermsMap?: Map<string, { description: string; displayName: string }>
): TagItem | null => {
  const glossaryInfo = glossaryTermsMap?.get(fqn);

  if (!glossaryInfo) {
    return null;
  }

  const nameParts = fqn.split('.');
  const name = removeFqnQuotes(nameParts[nameParts.length - 1]);

  return {
    tagFQN: fqn,
    name: glossaryInfo.displayName || name,
    displayName: glossaryInfo.displayName || '',
    description: glossaryInfo.description || '',
    style: {},
    source: 'Glossary',
    labelType: 'Manual',
    state: 'Confirmed',
  };
};

export const generateGlossaryTermPatchOperations = (
  oldTags: TagItem[],
  newTermFQNs: string[],
  glossaryTermsMap?: Map<string, { description: string; displayName: string }>
): JsonPatchOperation[] => {
  const operations: JsonPatchOperation[] = [];

  const classificationTagsFromOld = oldTags.filter((tag) => tag.source === 'Classification');
  const glossaryTermsFromOld = oldTags.filter((tag) => tag.source === 'Glossary');

  const existingTermFQNs = new Set(glossaryTermsFromOld.map((t) => t.tagFQN));
  const newTermFQNsSet = new Set(newTermFQNs);

  const keptGlossaryTerms = glossaryTermsFromOld.filter((term) => newTermFQNsSet.has(term.tagFQN));

  const newGlossaryTerms: TagItem[] = newTermFQNs
    .filter((fqn) => !existingTermFQNs.has(fqn))
    .map((fqn) => createGlossaryTermFromFQN(fqn, glossaryTermsMap))
    .filter((tag): tag is TagItem => tag !== null);

  const allGlossaryTerms = [...keptGlossaryTerms, ...newGlossaryTerms];
  const newAllTags = [...allGlossaryTerms, ...classificationTagsFromOld];

  const maxLength = Math.max(oldTags.length, newAllTags.length);

  for (let i = 0; i < maxLength; i++) {
    const oldTag = oldTags[i];
    const newTerm = newAllTags[i];

    if (!oldTag && newTerm) {
      operations.push({
        op: 'add',
        path: `/tags/${i}`,
        value: newTerm,
      });
    } else if (oldTag && !newTerm) {
      operations.push({
        op: 'remove',
        path: `/tags/${i}`,
      });
    } else if (oldTag && newTerm) {
      const fieldOps = generateTagFieldOperations(`/tags/${i}`, newTerm);
      const displayNameOps = generateDisplayNameOperations(`/tags/${i}`, oldTag.displayName, newTerm.displayName);
      const styleOps = generateStyleOperations(`/tags/${i}`, oldTag.style, newTerm.style);

      operations.push(...fieldOps, ...displayNameOps, ...styleOps);
    }
  }

  const removeOps = operations.filter((op) => op.op === 'remove');
  const otherOps = operations.filter((op) => op.op !== 'remove');

  removeOps.sort((a, b) => {
    const aIndex = parseInt(a.path.split('/')[2]);
    const bIndex = parseInt(b.path.split('/')[2]);
    return bIndex - aIndex;
  });

  return [...removeOps, ...otherOps];
};
